import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';

interface FlaggedFile {
  PK: string;
  SK: string;
  systemId: string;
  recordDate: string;
  originalFilename: string;
  archivePath: string;
  normalizedFilename: string;
  agentId: string;
  callerId: string;
  callerPhone: string;
  fileSizeBytes: number;
  ingestTimestamp: string;
  missingFields: string[];
}

interface QuarantinedFile {
  systemId: string;
  recordDate: string;
  originalFilename: string;
  errorReason: string;
  ingestTimestamp: string;
  fileSizeBytes: number;
}

interface DownloadFailure {
  PK: string;
  SK: string;
  recordingId: string;
  systemId: string;
  recordDate: string;
  originalFilename: string;
  ingestTimestamp: string;
  callerPhone: string;
  agentId: string;
  fileSizeBytes: number;
  agentName: string;
  locationName: string;
  contactName: string;
  contactEmail: string;
  contactId: string;
  callDurationSec: number;
  callDirection: string;
  ghlLocationId: string;
  ghlCallId: string;
  errorReason: string;
  retryAttempts: number;
  lastRetryAt: string;
}

interface NoRecording {
  PK: string;
  SK: string;
  recordingId: string;
  systemId: string;
  recordDate: string;
  originalFilename: string;
  ingestTimestamp: string;
  callerPhone: string;
  agentId: string;
  agentName: string;
  contactName: string;
  contactEmail: string;
  contactId: string;
  terminationReason: string;
  terminatedAt: string;
  callDurationSec: number;
  callDirection: string;
  ghlLocationId: string;
  ghlCallId: string;
}

interface MissingPitLocation {
  ghlLocationId: string;
  locationName: string;
  count: number;
  latest: string;
}

interface MissedCallCampaign {
  campaign: string;
  count: number;
  voicemail: number;
  hold_no_answer: number;
}

interface MissedCall {
  PK: string;
  SK: string;
  systemId: string;
  recordDate: string;
  callTimestamp: string;
  callerPhone: string;
  campaign: string;
  disposition: string;
  archivePath: string;
  originalFilename: string;
}

interface UploadFailure {
  PK: string;
  SK: string;
  recordingId: string;
  systemId: string;
  recordDate: string;
  originalFilename: string;
  ingestTimestamp: string;
  callerPhone: string;
  agentId: string;
  agentName: string;
  sourceUser: string;
  failureStage: string;
  failureError: string;
  failedAt: string;
  fileName: string;
  fileSizeBytes: number;
  attemptedBy: string;
}

const SYSTEM_LABELS: Record<string, string> = {
  pulsedial: 'PulseDial',
  vicidial: 'Vicidial',
  dialedin: 'DialedIn',
  '3cx-legacy': '3CX Legacy',
};

function Quarantine() {
  const [items, setItems] = useState<QuarantinedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [systemFilter, setSystemFilter] = useState('');
  const [, setCount] = useState(0);
  const [flagged, setFlagged] = useState<FlaggedFile[]>([]);
  const [, setFlaggedCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'flagged' | 'quarantine' | 'failures' | 'uploads' | 'noRecording' | 'missingPit' | 'missedCalls'>('flagged');
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [failures, setFailures] = useState<DownloadFailure[]>([]);
  const [uploadFailures, setUploadFailures] = useState<UploadFailure[]>([]);
  const [noRecordings, setNoRecordings] = useState<NoRecording[]>([]);
  const [missingPitLocations, setMissingPitLocations] = useState<MissingPitLocation[]>([]);
  const [missingPitCount, setMissingPitCount] = useState(0);
  const [missedCallCampaigns, setMissedCallCampaigns] = useState<MissedCallCampaign[]>([]);
  const [missedCallCount, setMissedCallCount] = useState(0);
  const [missedCalls, setMissedCalls] = useState<MissedCall[]>([]);
  const [missedVoicemailOnly, setMissedVoicemailOnly] = useState(true);
  // True when any of the tab fetches rejected — surfaces a visible banner
  // instead of silently rendering the failed list(s) as healthy-empty.
  const [loadError, setLoadError] = useState(false);
  const [loadErrorDismissed, setLoadErrorDismissed] = useState(false);
  // Per-endpoint hasMore flags (backend sets hasMore: true when its fetch cap
  // was hit) so tab badges can show "N+" instead of a misleading exact N.
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  // Per-row state for the inline agent-attribution UI on the Flagged tab.
  // Keyed by `${PK}|${SK}` since that's globally unique. Listener types in the
  // agent ID after listening, then Save updates DDB + drops the row from the tab.
  const [agentInputs, setAgentInputs] = useState<Record<string, string>>({});
  const [attributingIds, setAttributingIds] = useState<Set<string>>(new Set());
  // Distinct agents for the attribute-dropdown, loaded once when the user
  // first lands on the Quarantine page. Keyed by systemId so cross-system
  // agents don't bleed into each other's lists. Populated from the existing
  // pre-aggregated FILTERS#<system> stats item — no full-table Scan needed.
  const [agentOptionsBySystem, setAgentOptionsBySystem] = useState<Record<string, string[]>>({});

  // Load dismissed items from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('dismissed-quarantine');
      if (stored) setDismissedIds(new Set(JSON.parse(stored)));
    } catch { /* ok */ }
  }, []);

  const dismiss = (id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev).add(id);
      localStorage.setItem('dismissed-quarantine', JSON.stringify(Array.from(next)));
      window.dispatchEvent(new Event('quarantine-dismissed'));
      return next;
    });
  };

  // Dismissal key includes system + date so dismissing one row can't hide
  // same-named rows from other systems or dates.
  const flagKey = (f: FlaggedFile) => `flag_${f.systemId}_${f.recordDate}_${f.originalFilename}`;

  const dismissAll = (type: 'flagged' | 'quarantine') => {
    const ids = type === 'flagged'
      ? flagged.map(f => flagKey(f))
      : items.map(q => `quar_${q.originalFilename}`);
    setDismissedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      localStorage.setItem('dismissed-quarantine', JSON.stringify(Array.from(next)));
      window.dispatchEvent(new Event('quarantine-dismissed'));
      return next;
    });
  };

  const loadData = (system?: string) => {
    setLoading(true);
    const params: any = {};
    if (system) params.systemId = system;
    // Track which fetches reject so we can show an error banner instead of
    // silently rendering a failed list as healthy-empty. Each failed fetch
    // still falls back to empty so the tabs that DID load render normally.
    let anyFailed = false;
    const fallback = <T,>(empty: T) => (): T => { anyFailed = true; return empty; };
    Promise.all([
      api.getQuarantine(params).catch(fallback({ quarantined: [], count: 0 })),
      api.getFlaggedFiles(system).catch(fallback({ flagged: [], count: 0 })),
      api.getDownloadFailures().catch(fallback({ failures: [], count: 0 })),
      api.getUploadFailures().catch(fallback({ failures: [], count: 0 })),
      api.getNoRecordings().catch(fallback({ recordings: [], count: 0 })),
      api.getMissingPit().catch(fallback({ recordings: [], count: 0, locations: [] })),
      api.getMissedCalls().catch(fallback({ recordings: [], count: 0, campaigns: [] })),
    ]).then(([qData, fData, dfData, ufData, nrData, mpData, mcData]: any[]) => {
      setItems(qData.quarantined || []);
      setCount(qData.count || 0);
      setFlagged(fData.flagged || []);
      setFlaggedCount(fData.count || 0);
      setFailures(dfData.failures || []);
      setUploadFailures(ufData.failures || []);
      setNoRecordings(nrData.recordings || []);
      setMissingPitLocations(mpData.locations || []);
      setMissingPitCount(mpData.count || 0);
      setMissedCallCampaigns(mcData.campaigns || []);
      setMissedCallCount(mcData.count || 0);
      setMissedCalls(mcData.recordings || []);
      setHasMore({
        quarantine: qData.hasMore === true,
        flagged: fData.hasMore === true,
        failures: dfData.hasMore === true,
        uploads: ufData.hasMore === true,
        noRecording: nrData.hasMore === true,
        missingPit: mpData.hasMore === true,
        missedCalls: mcData.hasMore === true,
      });
      setLoadError(anyFailed);
      setLoadErrorDismissed(false);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(systemFilter || undefined); }, [systemFilter]);

  // Load the distinct-agents list per system once. Pulls from the
  // pre-aggregated FILTERS#<system> stats item — cheap, no full Scan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const systems = ['vicidial', 'dialedin', 'pulsedial', 'leadmomentum'];
      const results = await Promise.all(systems.map(s =>
        api.getSearchFilters({ systemId: s }).catch(() => ({ agents: [] as string[] }))
      ));
      if (cancelled) return;
      const out: Record<string, string[]> = {};
      systems.forEach((s, i) => {
        out[s] = (results[i].agents || []).filter((a: string) => a && a !== 'unknown');
      });
      setAgentOptionsBySystem(out);
    })();
    return () => { cancelled = true; };
  }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleRetry = async (f: DownloadFailure) => {
    setRetryingIds(prev => new Set(prev).add(f.recordingId));
    try {
      await api.retryDownload(f.PK, f.SK);
      showMessage(`Retry queued for ${f.recordingId.slice(0, 16)}… — check back in a few minutes`, 'success');
    } catch (err: any) {
      showMessage(`Retry failed: ${err.message || err}`, 'error');
    } finally {
      setRetryingIds(prev => { const next = new Set(prev); next.delete(f.recordingId); return next; });
    }
  };

  const handleDeleteFailure = async (f: DownloadFailure) => {
    if (!window.confirm(`Permanently delete this orphaned record?\n\nFilename: ${f.originalFilename}\n\nThis only deletes the DynamoDB record (the S3 object is already 0 bytes). Cannot be undone.`)) return;
    try {
      await api.deleteFailedRecording(f.PK, f.SK);
      setFailures(prev => prev.filter(x => x.PK !== f.PK || x.SK !== f.SK));
      showMessage(`Deleted ${f.recordingId.slice(0, 16)}…`, 'success');
    } catch (err: any) {
      showMessage(`Delete failed: ${err.message || err}`, 'error');
    }
  };

  const handleDeleteUploadFailure = async (f: UploadFailure) => {
    if (!window.confirm(`Permanently delete this failed-upload record?\n\nConfirmation #: ${f.recordingId}\nStage: ${f.failureStage}\nError: ${f.failureError}\n\nThis only deletes the DynamoDB row. The user can re-attempt the upload from the Upload Calls page. Cannot be undone.`)) return;
    try {
      await api.deleteFailedRecording(f.PK, f.SK);
      setUploadFailures(prev => prev.filter(x => x.PK !== f.PK || x.SK !== f.SK));
      showMessage(`Deleted ${f.recordingId}`, 'success');
    } catch (err: any) {
      showMessage(`Delete failed: ${err.message || err}`, 'error');
    }
  };

  const handleAttributeAgent = async (f: FlaggedFile) => {
    const rowKey = `${f.PK}|${f.SK}`;
    const agentValue = (agentInputs[rowKey] || '').trim();
    if (!agentValue) {
      showMessage('Enter an agent ID first', 'error');
      return;
    }
    setAttributingIds(prev => new Set(prev).add(rowKey));
    try {
      await api.attributeAgent(f.PK, f.SK, agentValue);
      // Drop the row from the local list — server has already removed
      // attentionStatus so the next poll wouldn't return it anyway.
      setFlagged(prev => prev.filter(x => `${x.PK}|${x.SK}` !== rowKey));
      setAgentInputs(prev => { const next = { ...prev }; delete next[rowKey]; return next; });
      showMessage(`Attributed agent ${agentValue} to ${f.originalFilename.slice(0, 50)}`, 'success');
    } catch (err: any) {
      showMessage(`Attribute failed: ${err.message || err}`, 'error');
    } finally {
      setAttributingIds(prev => { const next = new Set(prev); next.delete(rowKey); return next; });
    }
  };

  const exportFlaggedCsv = () => {
    const headers = ['Date', 'System', 'Filename', 'Agent', 'Phone', 'Missing Fields', 'Ingested At'];
    const rows = flagged.map(f => [
      f.recordDate, f.systemId, f.originalFilename, f.agentId || '', f.callerId || '',
      f.missingFields.join(', '), f.ingestTimestamp || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `flagged-files-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Quarantine</h1>
        <p>Files that failed validation during ingestion</p>
      </div>

      {loadError && !loadErrorDismissed && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: '#ffebee', border: '1px solid #ef9a9a', fontSize: 13, color: '#c62828' }}>
          <span>
            Some failure lists couldn't be loaded — counts may be incomplete.{' '}
            <button type="button" onClick={() => loadData(systemFilter || undefined)}
              style={{ padding: '2px 10px', border: '1px solid #c62828', borderRadius: 4, background: '#fff', color: '#c62828', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Retry
            </button>
          </span>
          <button type="button" onClick={() => setLoadErrorDismissed(true)} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '16px' }}>
        <button type="button" onClick={() => setActiveTab('flagged')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'flagged' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'flagged' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'flagged' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px' }}>
          Incomplete Metadata ({flagged.filter(f => !dismissedIds.has(flagKey(f))).length}{hasMore.flagged ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('quarantine')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'quarantine' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'quarantine' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'quarantine' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px' }}>
          Quarantined ({items.filter(q => !dismissedIds.has(`quar_${q.originalFilename}`)).length}{hasMore.quarantine ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('failures')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'failures' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'failures' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'failures' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px' }}>
          Download Failures ({failures.length}{hasMore.failures ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('uploads')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'uploads' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'uploads' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'uploads' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px' }}>
          Upload Failures ({uploadFailures.length}{hasMore.uploads ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('noRecording')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'noRecording' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'noRecording' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'noRecording' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px', color: '#666' }}>
          No Recording ({noRecordings.length}{hasMore.noRecording ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('missingPit')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'missingPit' ? '2px solid #ff9800' : '1px solid #ddd', background: activeTab === 'missingPit' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'missingPit' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px', color: missingPitLocations.length ? '#e65100' : '#666' }}>
          Missing PIT ({missingPitLocations.length}{hasMore.missingPit ? '+' : ''})
        </button>
        <button type="button" onClick={() => setActiveTab('missedCalls')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'missedCalls' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'missedCalls' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'missedCalls' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: '14px', color: '#666' }}>
          Missed Calls ({missedCallCount}{hasMore.missedCalls ? '+' : ''})
        </button>
      </div>

      {message && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828' }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <select
          value={systemFilter}
          onChange={e => setSystemFilter(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
        >
          <option value="">All Systems</option>
          <option value="pulsedial">PulseDial</option>
          <option value="vicidial">Vicidial</option>
          <option value="dialedin">DialedIn</option>
        </select>
      </div>

      {/* Flagged Files Tab */}
      {/* Datalists for the per-system attribute-agent dropdown — referenced
          via `list={`agents-${systemId}`}` on the input. Anywhere in the
          document is fine; rendering once near the top keeps them simple. */}
      {Object.entries(agentOptionsBySystem).map(([sys, opts]) => (
        <datalist key={`agents-${sys}`} id={`agents-${sys}`}>
          {opts.map(a => <option key={a} value={a} />)}
        </datalist>
      ))}

      {activeTab === 'flagged' && !loading && (() => {
        const visibleFlagged = flagged.filter(f => !dismissedIds.has(flagKey(f)));
        return (
        <>
          {visibleFlagged.length === 0 ? (
            <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
              <p>No files with incomplete metadata. All recordings have the required fields.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>{visibleFlagged.length} file(s) with incomplete metadata</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={() => dismissAll('flagged')}
                  style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', fontSize: '13px', cursor: 'pointer' }}>
                  Dismiss All
                </button>
                <button type="button" onClick={exportFlaggedCsv}
                  style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', fontSize: '13px', cursor: 'pointer' }}>
                  Export CSV
                </button>
                </div>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>System</th>
                    <th>Filename</th>
                    <th>Phone</th>
                    <th>Missing</th>
                    <th>Size</th>
                    <th>Date</th>
                    <th>Play</th>
                    <th>Attribute Agent</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFlagged.map((f, i) => {
                    const rowKey = `${f.PK}|${f.SK}`;
                    const sizeKB = f.fileSizeBytes ? Math.round(f.fileSizeBytes / 1024) : 0;
                    const sizeStr = sizeKB >= 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`;
                    const agentMissing = f.missingFields.includes('agent');
                    const inputVal = agentInputs[rowKey] || '';
                    const saving = attributingIds.has(rowKey);
                    return (
                      <tr key={`${rowKey}-${i}`}>
                        <td><span className={`system-tag ${f.systemId === '3cx-legacy' ? 'legacy' : f.systemId}`}>{SYSTEM_LABELS[f.systemId] || f.systemId}</span></td>
                        <td style={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: '260px', wordBreak: 'break-all' }}>{f.originalFilename}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.callerPhone || '-'}</td>
                        <td>{f.missingFields.map(mf => <span key={mf} className="badge badge-warning" style={{ marginRight: '4px' }}>{mf}</span>)}</td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{sizeStr}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{f.ingestTimestamp ? new Date(f.ingestTimestamp).toLocaleDateString() : f.recordDate}</td>
                        <td>
                          <AudioPlayer systemId={f.systemId} archivePath={f.archivePath} filename={f.normalizedFilename || f.originalFilename} />
                        </td>
                        <td>
                          {agentMissing ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <input
                                type="text"
                                value={inputVal}
                                list={`agents-${f.systemId}`}
                                onChange={e => setAgentInputs(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter' && !saving) handleAttributeAgent(f); }}
                                placeholder="pick or type"
                                style={{ width: 110, padding: '3px 6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3 }}
                                disabled={saving}
                              />
                              <button type="button" onClick={() => handleAttributeAgent(f)} disabled={saving || !inputVal.trim()}
                                style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #00bcd4', borderRadius: 3, background: saving ? '#e0f7fa' : '#fff', color: '#00838f', cursor: saving ? 'wait' : 'pointer' }}>
                                {saving ? '...' : 'Save'}
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#888' }}>—</span>
                          )}
                        </td>
                        <td>
                          <button type="button" onClick={() => dismiss(flagKey(f))}
                            style={{ padding: '3px 8px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#888' }}>
                            Dismiss
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="setup-card" style={{ marginTop: '16px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
                  Export this list and send it to the dialer vendor to request corrected filenames.
                  Files are still stored and playable — they just have incomplete metadata for search and filtering.
                </p>
              </div>
            </>
          )}
        </>
        );
      })()}

      {/* Quarantine Tab */}
      {activeTab === 'quarantine' && loading ? (
        <div className="loading">Loading...</div>
      ) : activeTab === 'quarantine' && items.filter(q => !dismissedIds.has(`quar_${q.originalFilename}`)).length === 0 ? (
        <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
          <p>No quarantined files found. That's a good thing.</p>
        </div>
      ) : activeTab === 'quarantine' ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>System</th>
              <th>Original Filename</th>
              <th>Reason</th>
              <th>Date</th>
              <th>Size</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.filter(q => !dismissedIds.has(`quar_${q.originalFilename}`)).map((item, i) => (
              <tr key={i}>
                <td>
                  <span className={`system-tag ${item.systemId === '3cx-legacy' ? 'legacy' : item.systemId}`}>
                    {SYSTEM_LABELS[item.systemId] || item.systemId}
                  </span>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px', maxWidth: '300px', wordBreak: 'break-all' }}>
                  {item.originalFilename}
                </td>
                <td>
                  <span className="badge badge-danger">{item.errorReason}</span>
                </td>
                <td>{item.ingestTimestamp ? new Date(item.ingestTimestamp).toLocaleString() : item.recordDate}</td>
                <td>{item.fileSizeBytes ? `${Math.round(item.fileSizeBytes / 1024)} KB` : '-'}</td>
                <td>
                  <button type="button" onClick={() => dismiss(`quar_${item.originalFilename}`)}
                    style={{ padding: '3px 8px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#888' }}>
                    Dismiss
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {/* Download Failures Tab */}
      {activeTab === 'failures' && !loading && (
        failures.length === 0 ? (
          <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
            <p>No download failures. All ingested recordings have valid audio.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              {failures.length} record(s) have a DynamoDB entry but no usable audio (download from GHL failed).
              Click <strong>Retry</strong> to re-attempt the download (recordings may have been removed from GHL — older failures less likely to recover).
              Click <strong>Delete</strong> to permanently remove an orphaned record we can't recover.
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Agent</th>
                  <th>Client</th>
                  <th>Phone</th>
                  <th>Dir</th>
                  <th>Error</th>
                  <th>Attempted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {failures.map((f, i) => {
                  // Same deep-link logic as the No Recording tab — gives the
                  // user a one-click jump to the GHL contact so they can
                  // manually verify whether a recording exists on GHL's side.
                  const lmBase = f.systemId === 'leadmomentum' ? 'https://app.leadmomentum.net' : 'https://app.gohighlevel.com';
                  const lmUrl = f.ghlLocationId && f.contactId
                    ? `${lmBase}/v2/location/${f.ghlLocationId}/contacts/detail/${f.contactId}`
                    : f.ghlLocationId
                      ? `${lmBase}/v2/location/${f.ghlLocationId}/conversations`
                      : null;
                  return (
                    <tr key={`${f.PK}-${f.SK}-${i}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{f.recordDate}</td>
                      <td style={{ fontSize: 12 }}>
                        {f.agentName || f.locationName || <span style={{ color: '#888', fontFamily: 'monospace', fontSize: 10 }}>{f.agentId || '—'}</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {f.contactName || <span style={{ color: '#888' }}>—</span>}
                        {f.contactEmail ? <div style={{ fontSize: 10, color: '#888' }}>{f.contactEmail}</div> : null}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.callerPhone || '-'}</td>
                      <td style={{ fontSize: 11, color: '#666' }}>{f.callDirection || '-'}</td>
                      <td style={{ fontSize: 11, color: '#c62828', maxWidth: 280, wordBreak: 'break-word' }}>{f.errorReason || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#888' }}>
                        {f.ingestTimestamp ? new Date(f.ingestTimestamp).toLocaleString() : '-'}
                        {f.retryAttempts > 0 ? <div style={{ fontSize: 10 }}>{f.retryAttempts} retry{f.retryAttempts === 1 ? '' : 's'}</div> : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {lmUrl ? (
                          <a href={lmUrl} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #ce93d8', borderRadius: 4, background: '#fff', color: '#6a1b9a', cursor: 'pointer', marginRight: 6, textDecoration: 'none', display: 'inline-block' }}>
                            Open in GHL
                          </a>
                        ) : null}
                        <button type="button" onClick={() => handleRetry(f)} disabled={retryingIds.has(f.recordingId)}
                          style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #00bcd4', borderRadius: 4, background: retryingIds.has(f.recordingId) ? '#e0f7fa' : '#fff', color: '#00838f', cursor: retryingIds.has(f.recordingId) ? 'wait' : 'pointer', marginRight: 6 }}>
                          {retryingIds.has(f.recordingId) ? 'Queued...' : 'Retry'}
                        </button>
                        <button type="button" onClick={() => handleDeleteFailure(f)}
                          style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #ef9a9a', borderRadius: 4, background: '#fff', color: '#c62828', cursor: 'pointer' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="setup-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                These are LeadMomentum (GHL) recordings where the webhook fired but the actual audio file never finished downloading from GoHighLevel.
                The most common cause is a race condition — the recording asset wasn't yet available at GHL when we tried to fetch.
                Retries can take a few minutes since the GHL processor uses exponential backoff. Refresh this page to see updated status.
              </p>
            </div>
          </>
        )
      )}

      {/* Upload Failures Tab */}
      {activeTab === 'uploads' && !loading && (
        uploadFailures.length === 0 ? (
          <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
            <p>No upload failures. Manually-uploaded calls have all completed successfully.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              {uploadFailures.length} record(s) failed during the Upload Calls flow. The DynamoDB row was created but the audio never reached S3 (or the S3 upload couldn't be confirmed).
              The user who attempted the upload can <strong>re-run the Upload Calls flow</strong> for that confirmation #. Click <strong>Delete</strong> to remove an orphaned record.
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>System</th>
                  <th>Confirmation #</th>
                  <th>Date</th>
                  <th>Agent</th>
                  <th>Stage</th>
                  <th>Error</th>
                  <th>File</th>
                  <th>Attempted By</th>
                  <th>Failed At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {uploadFailures.map((f, i) => {
                  const sizeKB = f.fileSizeBytes ? `${(f.fileSizeBytes / 1024 / 1024).toFixed(1)} MB` : '—';
                  return (
                    <tr key={`${f.PK}-${f.SK}-${i}`}>
                      <td><span className={`system-tag ${f.systemId === '3cx-legacy' ? 'legacy' : f.systemId}`}>{SYSTEM_LABELS[f.systemId] || f.systemId}</span></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{f.recordingId}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{f.recordDate}</td>
                      <td>{f.agentName || f.agentId || '—'}</td>
                      <td><span className="badge badge-warning">{f.failureStage}</span></td>
                      <td style={{ fontSize: 11, color: '#c62828', maxWidth: 280, wordBreak: 'break-word' }}>{f.failureError}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 10, maxWidth: 200, wordBreak: 'break-all' }}>
                        {f.fileName || '—'}<br />
                        <span style={{ color: '#888' }}>{sizeKB}</span>
                      </td>
                      <td style={{ fontSize: 11, color: '#666' }}>{f.attemptedBy || f.sourceUser || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#888' }}>
                        {f.failedAt ? new Date(f.failedAt).toLocaleString() : '—'}
                      </td>
                      <td>
                        <button type="button" onClick={() => handleDeleteUploadFailure(f)}
                          style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #ef9a9a', borderRadius: 4, background: '#fff', color: '#c62828', cursor: 'pointer' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="setup-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                <strong>Common causes:</strong> WAV files larger than ~500&nbsp;MB can OOM the browser tab during conversion;
                S3 pre-signed URLs expire 1 hour after the batch starts (slow connections + large files can run out the clock);
                page refreshes mid-upload abandon in-flight rows.
                Server-side: every failure is also logged to CloudWatch with prefix <code>UPLOAD_FAILURE</code>.
              </p>
            </div>
          </>
        )
      )}

      {/* No Recording Tab — terminal records, audit-only */}
      {activeTab === 'noRecording' && !loading && (
        noRecordings.length === 0 ? (
          <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
            <p>No terminal-state records yet. Records land here only after the courtesy retry confirms no audio exists at the source.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              {noRecordings.length} record(s) where the source system (currently GHL/LeadMomentum) confirmed no audio asset exists.
              These have been retried and are <strong>terminal</strong> — no further automatic attempts will be made.
              Visible here for audit; they do not contribute to the alert badge.
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Agent</th>
                  <th>Client</th>
                  <th>Phone</th>
                  <th>Duration</th>
                  <th>Direction</th>
                  <th>Reason</th>
                  <th>Terminated</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                {noRecordings.map((r, i) => {
                  const dur = r.callDurationSec
                    ? r.callDurationSec < 60 ? `${r.callDurationSec}s` : `${Math.floor(r.callDurationSec / 60)}m ${r.callDurationSec % 60}s`
                    : '—';
                  // LeadMomentum is white-labeled at app.leadmomentum.net.
                  // Deep-link to the contact detail page (/contacts/detail/<contactId>)
                  // rather than the agency-wide conversations dashboard.
                  // Falls back to conversations page if we don't have a contactId.
                  const lmBase = r.systemId === 'leadmomentum' ? 'https://app.leadmomentum.net' : 'https://app.gohighlevel.com';
                  const lmUrl = r.ghlLocationId && r.contactId
                    ? `${lmBase}/v2/location/${r.ghlLocationId}/contacts/detail/${r.contactId}`
                    : r.ghlLocationId
                      ? `${lmBase}/v2/location/${r.ghlLocationId}/conversations`
                      : null;
                  return (
                    <tr key={`${r.PK}-${r.SK}-${i}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.recordDate}</td>
                      <td style={{ fontSize: 12 }}>{r.agentName || <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={{ fontSize: 12 }}>
                        {r.contactName || <span style={{ color: '#aaa' }}>—</span>}
                        {r.contactEmail && <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>{r.contactEmail}</div>}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.callerPhone || '-'}</td>
                      <td style={{ fontSize: 12 }}>{dur}</td>
                      <td style={{ fontSize: 12, color: '#666' }}>{r.callDirection || '-'}</td>
                      <td><span className="badge badge-warning" style={{ fontSize: 11 }}>{r.terminationReason}</span></td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#888' }}>
                        {r.terminatedAt ? new Date(r.terminatedAt).toLocaleString() : '—'}
                      </td>
                      <td style={{ fontSize: 11 }}>
                        {lmUrl ? (
                          <a href={lmUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#00838f' }}>
                            {r.contactId ? 'Open contact' : 'Open in GHL'}
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="setup-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                <strong>What this means:</strong> the call took place and we have its metadata, but the source system (GHL) returned
                <code> 422 "Message does not have recording" </code>
                on the recording endpoint after a courtesy retry. Common causes: hangup before any audio was captured, recording disabled
                for that workflow/number, or a source-side recording-pipeline failure. The DynamoDB row stays for compliance retention.
              </p>
            </div>
          </>
        )
      )}

      {/* Missing PIT Tab — locations sending webhooks with no PIT in our key map */}
      {activeTab === 'missingPit' && !loading && (
        missingPitLocations.length === 0 ? (
          <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
            <p>No unconfigured locations — every GHL location sending calls has a PIT.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              {missingPitLocations.length} GHL location(s) are sending call webhooks but have <strong>no PIT</strong> in our key map,
              so their recordings can't be fetched (<strong>{missingPitCount}</strong> call(s) waiting). Onboard each below.
            </div>
            <p style={{ fontSize: 13 }}>
              <Link to="/ghl-locations">Manage locations (add PIT / mute) →</Link>
            </p>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Location ID</th>
                  <th>Calls waiting</th>
                  <th>Latest</th>
                </tr>
              </thead>
              <tbody>
                {missingPitLocations.map((l, i) => (
                  <tr key={`${l.ghlLocationId}-${i}`}>
                    <td style={{ fontSize: 13 }}>{l.locationName || <span style={{ color: '#aaa' }}>—</span>}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.ghlLocationId}</td>
                    <td><span className="badge badge-warning" style={{ fontSize: 11 }}>{l.count}</span></td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#888' }}>{l.latest ? new Date(l.latest).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="setup-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                <strong>To onboard a location:</strong> create a GHL Private Integration Token (PIT) for it with the 4 read-only scopes
                (<code>contacts.readonly</code>, <code>conversations.readonly</code>, <code>conversations/message.readonly</code>, <code>locations.readonly</code>),
                add it to the <code>pulse-storage-ghl-location-keys-v1</code> SSM map, then run
                <code> python scripts/ghl_recover_no_recording.py --all --execute --include-medium </code>
                to recover the waiting calls.
              </p>
            </div>
          </>
        )
      )}

      {/* Missed Calls Tab — inbound voicemail / no-answer calls (nobody available) */}
      {activeTab === 'missedCalls' && !loading && (
        missedCallCampaigns.length === 0 ? (
          <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
            <p>No missed calls recorded.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              <strong>{missedCallCount}</strong> inbound call(s) that hit voicemail or rang with <strong>no agent available</strong>,
              grouped by lead campaign. These have no agent because nobody answered — they're informational (not a metadata gap to fix).
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Total</th>
                  <th>Voicemail</th>
                  <th>Hold / no-answer</th>
                </tr>
              </thead>
              <tbody>
                {missedCallCampaigns.map((c, i) => (
                  <tr key={`${c.campaign}-${i}`}>
                    <td style={{ fontSize: 13 }}>{c.campaign || <span style={{ color: '#aaa' }}>—</span>}</td>
                    <td><span className="badge badge-warning" style={{ fontSize: 11 }}>{c.count}</span></td>
                    <td style={{ fontSize: 12 }}>{c.voicemail}</td>
                    <td style={{ fontSize: 12 }}>{c.hold_no_answer}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Per-call list with playback — voicemails are potential leads */}
            <div style={{ margin: '16px 0 8px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <h4 style={{ margin: 0, fontSize: 13, color: 'var(--text-heading)' }}>Individual calls</h4>
              <label style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                <input type="checkbox" checked={missedVoicemailOnly} onChange={e => setMissedVoicemailOnly(e.target.checked)} style={{ marginRight: 4 }} />
                Voicemails only (potential leads)
              </label>
              <span style={{ fontSize: 12, color: '#999' }}>
                {missedCalls.filter(c => !missedVoicemailOnly || c.disposition === 'voicemail').length} shown
              </span>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8 }}>
              <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Callback #</th>
                    <th>Campaign</th>
                    <th>Type</th>
                    <th>Listen</th>
                  </tr>
                </thead>
                <tbody>
                  {missedCalls.filter(c => !missedVoicemailOnly || c.disposition === 'voicemail').map((c, i) => {
                    const ts = (c.callTimestamp || '').split('T');
                    const isVm = c.disposition === 'voicemail';
                    return (
                      <tr key={`${c.PK}-${c.SK}-${i}`} style={{ background: isVm ? '#f1f8e9' : undefined }}>
                        <td style={{ whiteSpace: 'nowrap' }}>{ts[0] || c.recordDate}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{ts[1] || ''}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.callerPhone || '—'}</td>
                        <td style={{ fontSize: 10 }}>{c.campaign}</td>
                        <td><span className="badge" style={{ fontSize: 10, background: isVm ? '#c8e6c9' : '#eee', color: isVm ? '#2e7d32' : '#777' }}>{isVm ? 'voicemail' : 'hold/no-answer'}</span></td>
                        <td><AudioPlayer systemId={c.systemId} archivePath={c.archivePath} filename={c.originalFilename} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="setup-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                <strong>Voicemails are potential leads</strong> — the caller reached voicemail (nobody answered) and may have left a
                message about coverage. Listen and call back the number if it's recent. The hold/no-answer ones are hold music with no
                message. Classified from the call audio; useful as evidence for the lead vendor too.
              </p>
            </div>
          </>
        )
      )}

      {activeTab === 'quarantine' && (
      <div className="setup-card" style={{ marginTop: '24px' }}>
        <h3>What to do with quarantined files</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>Unsupported format</strong> — ask the vendor to resend as MP3 or WAV</li>
          <li><strong>File too small</strong> — file may be corrupt or empty, ask for a resend</li>
          <li><strong>Duplicate</strong> — file was already ingested, no action needed</li>
          <li><strong>Missing required fields</strong> — filename doesn't match expected format, ask vendor to fix</li>
        </ul>
        <p style={{ marginTop: '12px', fontSize: '13px', color: '#888' }}>
          Quarantined files are preserved on the NAS at <code>/volume1/archive/&lt;system&gt;/quarantine/</code> and can be manually re-processed after the issue is resolved.
        </p>
      </div>
      )}
    </div>
  );
}

export default Quarantine;
